import { useState } from 'react';
import { Plus, Building2 } from 'lucide-react';
import api from '../../services/api';
import { AddToCrmModal } from '../AddToCrmModal';
import { ContactNotesTasksModal } from '../ContactNotesTasksModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADES = [
  ['groundworks', 'Groundworks'], ['brickwork', 'Brickwork'], ['sfs', 'SFS'],
  ['roofing', 'Roofing'], ['cladding', 'Cladding'], ['m_and_e', 'M&E'],
  ['fire_protection', 'Fire Protection'], ['drylining', 'Drylining'],
  ['screeding', 'Screeding'], ['ceilings', 'Ceilings'],
];

/** Always-visible packages + subcontractors sub-row under each Spellers project —
 * every chip is a real, live Sub_Contractor_Packages CRM record. Adding a
 * contractor reuses the exact same "Add to CRM" modal used everywhere else in
 * the app to add a contractor to a project, so the actual package type the user
 * picks in CRM is what determines which trade box the chip lands in. */
export const SpellersPackagesSection = ({ project, onUpdated }) => {
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [customerModalAccount, setCustomerModalAccount] = useState(null);
  const links = project.package_links || {};

  const refreshFromCrm = async () => {
    try {
      await api.post(`${API}/spellers/projects/${project.id}/sync-packages`);
      const res = await api.get(`${API}/spellers/projects/${project.id}`);
      onUpdated?.(res.data.project);
    } catch (_) { /* non-fatal — next full list reload will pick it up */ }
  };

  const initialProject = {
    id: project.zoho_project_id,
    deal_name: project.project_name,
    postcode: project.postcode,
    stage: project.stage,
  };

  return (
    <div className="px-4 py-3 bg-slate-50 border-l-4 border-orange-300" data-testid={`spellers-packages-section-${project.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Packages</span>
        <button
          onClick={() => setShowAddContractor(true)}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-orange-700 bg-orange-50 border border-orange-300 rounded-full px-2.5 py-1 hover:bg-orange-100 transition-colors flex-shrink-0 whitespace-nowrap"
          data-testid={`spellers-add-contractor-btn-${project.id}`}
        >
          <Plus className="w-3 h-3" /> Add Contractor
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {TRADES.map(([key, label]) => {
          const entries = links[key] || [];
          return (
            <div key={key} className="bg-white rounded-lg border-2 border-gray-200 px-2 py-1.5 min-w-0" data-testid={`spellers-package-trade-${project.id}-${key}`}>
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</div>
              <div className="flex flex-wrap gap-1 items-center">
                {entries.length === 0 && <span className="text-[11px] text-gray-300">—</span>}
                {entries.map((e) => (
                  <button
                    key={e.sub_contractor_package_id}
                    onClick={() => setCustomerModalAccount({ id: e.zoho_account_id, name: e.name })}
                    className="inline-flex items-center gap-1 max-w-full text-left text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200 transition-colors"
                    data-testid={`spellers-package-chip-${project.id}-${key}-${e.sub_contractor_package_id}`}
                  >
                    <Building2 className="w-2.5 h-2.5" /> {e.name}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showAddContractor && (
        <AddToCrmModal
          isOpen={showAddContractor}
          onClose={() => { setShowAddContractor(false); refreshFromCrm(); }}
          fastTrack
          initialMode="project"
          initialProject={initialProject}
          zIndex={100}
        />
      )}

      <ContactNotesTasksModal
        isOpen={!!customerModalAccount}
        onClose={() => setCustomerModalAccount(null)}
        initialAccount={customerModalAccount}
      />
    </div>
  );
};

export default SpellersPackagesSection;
